import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {  Publish, Backup, Check  } from "@mui/icons-material";
import { 
	Button,
	FormControl,
	FormLabel,
	FormControlLabel,
	RadioGroup,
	Radio,
	TextField,
	Typography,
	CircularProgress,
	Alert
 } from "@mui/material";
import { Dispatch } from 'redux';
import { ReduxComponentInterface } from 'edocatbase/Global';
import { revertVersion, uploadNewVersion } from '../../actions';
import { last } from 'ramda';
import { getFile } from '../../utils';
import { toggleModal } from '../../../ModalTest/actions';
import Modal from '../../../ModalTest';

interface NewVersionModalProps extends ReduxComponentInterface {
	items: any;
	site: string;
	callback: Function;
	disallowedExtensions: string[];
	isOpen: boolean;
}

interface NewVersionModalState {
	majorVersion: boolean;
	description: string;
	file: any;
	status: 'initial' | 'done' | 'error';
	message: string;
	loading: boolean;
}

class NewVersionModal extends window.React.PureComponent<
	NewVersionModalProps,
	NewVersionModalState
> {
	constructor(props: NewVersionModalProps) {
		super(props);
		this.state = {
			majorVersion: false,
			description: '',
			file: props.items?.newFile || null,
			status: 'initial',
			message: '',
			loading: false,
		};
	}

	onModalClose = () => {
		const { actions, isOpen } = this.props;
		if (isOpen) {
			actions.toggleModal(false, 'newVersionModal');
		}
	};

	onUploadClick = (event: React.MouseEvent) => {
		event.preventDefault();
		this.setState({ loading: true }, async () => {
			const {
				items: { onUpload, index, originalFile, newFile, revertVersionData },
				callback,
			} = this.props;
			const { majorVersion, description, file } = this.state;

			const resolvedFile = file?.file ? await getFile(file) : file;

			const newVersionData = revertVersionData
				? { majorVersion: majorVersion ? 'true' : 'false', comment: description }
				: {
					nodeRef: originalFile.nodeRef,
					fileName: originalFile.name,
					majorVersion,
					desc: description,
				};

			if (revertVersionData) {
				try {
					await revertVersion(
						revertVersionData.nodeRef,
						revertVersionData.version,
						newVersionData
					);
				} catch (err: unknown) {
					const message =
						err instanceof Error &&
						(Number(err.message) === 409 || err.message === 'Conflict')
							? window.Alfresco.messages.global['edocat.revert-version.error']
							: window.Alfresco.messages.global['edocat.new-version-upload.error'];

					this.setState({
						status: 'error',
						message,
						loading: false,
					});
					return;
				}
			} else {
				if (newFile && onUpload) {
					onUpload(null, [index], newVersionData);
				} else {
					try {
						await uploadNewVersion(
							resolvedFile,
							originalFile.nodeRef,
							majorVersion,
							description,
							() => {}
						);
					} catch (e) {
						this.setState({
							status: 'error',
							message:
								e || window.Alfresco.messages.global['edocat.new-version-upload.error'],
							loading: false,
						});
						return;
					}
				}
			}

			const successMessage = window.Alfresco.messages.global[
				revertVersionData ? 'edocat.revert.success' : 'edocat.upload.success'
				];

			this.setState({
				status: 'done',
				message: successMessage,
				loading: false,
			});

			setTimeout(() => {
				this.onModalClose();
			}, 1000);

			callback && callback();
		});
	};

	onDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ description: event.target.value });
	};

	onVersionTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ majorVersion: event.target.value === 'true' });
	};

	onFilesAdded = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files.length > 0) {
			const file = event.target.files[0];
			this.setState({
				file,
				message: '',
			});
			document.getElementById('uploadNewVersionInput').value = null;
		}
	};

	render() {
		const {
			items: {
				originalFile: { version },
				hideInput,
				revertVersionData,
			},
			disallowedExtensions,
		} = this.props;

		const { file, majorVersion, description, status, message, loading } = this.state;

		const VERSION = version.split('.');
		const fileDisallowed = file
			? disallowedExtensions.includes(last(file.name.split('.')))
			: false;

		const title = revertVersionData
			? window.Alfresco.messages.global['button.undo']
			: window.Alfresco.messages.global['edocat.document-version.update'];

		const buttonLabel = revertVersionData
			? window.Alfresco.messages.global['label.undo']
			: window.Alfresco.messages.global['actions.document.upload-new-version'];

		return (
			<Modal name="newVersionModal" title={title}>
				<div
					className="modal-body"
					style={{ width: '560px', textAlign: 'center' }}
				>
					{fileDisallowed && (
						<Typography
							variant="h6"
							sx={{ marginBottom: '20px' }}
						>
							{
								window.Alfresco.messages.global[
									'edocat.docDetail.disallowed-extensions.text'
									]
							}
						</Typography>
					)}

					{!hideInput && !revertVersionData && (
						<>
							<input
								style={{ display: 'none' }}
								onChange={this.onFilesAdded}
								id="uploadNewVersionInput"
								multiple={false}
								type="file"
							/>
							<label htmlFor="uploadNewVersionInput">
								<Button
									startIcon={<Backup color={'primary'} />}
									component="span"
								>
									{file
										? file.name
										: window.Alfresco.messages.global['label.upload-file']}
								</Button>
							</label>
						</>
					)}

					{status === 'error' && (
						<Alert
							severity="error"
							sx={{ justifyContent: 'center', margin: '1rem 0' }}
						>
							{message}
						</Alert>
					)}

					{status === 'done' && (
						<Alert
							severity="success"
							sx={{ justifyContent: 'center', margin: '1rem 0' }}
						>
							{message}
						</Alert>
					)}

					{!fileDisallowed && (
						<FormControl>
							<FormLabel align="left">
								{window.Alfresco.messages.global['label.version']}
							</FormLabel>
							<RadioGroup sx={{ my: 2 }}>
								<FormControlLabel
									label={`${window.Alfresco.messages.global['edocat.document-version.smallUpdate']} (${VERSION[0]}.${
										Number(VERSION[1]) + 1
									})`}
									value={false}
									control={
										<Radio
											color="primary"
											onChange={this.onVersionTypeChange}
											name="majorVersion"
											checked={!majorVersion}
										/>
									}
								/>
								<FormControlLabel
									label={`${window.Alfresco.messages.global['edocat.document-version.bigUpdate']} (${
										Number(VERSION[0]) + 1
									}.0)`}
									value={true}
									control={
										<Radio
											color="primary"
											name="majorVersion"
											checked={majorVersion}
											onChange={this.onVersionTypeChange}
										/>
									}
								/>
							</RadioGroup>
							<TextField
								sx={{ mt: 3 }}
								label={window.Alfresco.messages.global['label.comments']}
								name="description"
								onChange={this.onDescriptionChange}
								value={description}
								InputLabelProps={{ shrink: true }}
							/>
						</FormControl>
					)}
				</div>

				<div className="modal-actions">
					<Button
						startIcon={
							loading ? (
								<CircularProgress size="1rem" />
							) : status === 'done' ? (
								<Check />
							) : (
								<Publish />
							)
						}
						color="primary"
						disabled={
							(!file && !revertVersionData) || fileDisallowed || loading
						}
						onClick={this.onUploadClick}
						id="uploadNewVersionBtn"
					>
						{buttonLabel}
					</Button>
					<Button
						onClick={this.onModalClose}
						disabled={loading}
						id="cancelUploadNewVersionBtn"
					>
						{window.Alfresco.messages.global['button.close']}
					</Button>
				</div>
			</Modal>
		);
	}
}

const mapStateToProps = (state: any) => ({
	items: state.modal.newVersionModal?.items,
	isOpen: state.modal.newVersionModal?.isOpen,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	actions: bindActionCreators({ toggleModal }, dispatch),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(NewVersionModal);
